import { useCallback, useRef, useState } from "react";

import { FormControl, TextField } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { SkeletonWrapper } from "mui-toolkit/SkeletonWrapper";

const MIN_YEAR = dayjs().year() - 50;
const MAX_YEAR = dayjs().year() + 80;
const DATE_DISPLAY_FORMAT = "MM/DD/YYYY";

export const TilleyDatePicker = ({
  name,
  label,
  value,
  onChange,
  required = false,
  disabled = false,
  clearable = true,
  isLoading = false,
  onFieldTouched,
  errorText: externalErrorText,
  touched: externalTouched,
  showRequiredImmediately = false,
  slotProps = {},
  sx,
  className,
  ...otherProps
}) => {
  const [internalTouched, setInternalTouched] = useState(false);
  const [internalError, setInternalError] = useState(null);
  const [isFocused, setIsFocused] = useState(false);
  const [hasInputAttempt, setHasInputAttempt] = useState(false);
  const inputRef = useRef(null);

  const touched = externalTouched !== undefined ? externalTouched : internalTouched;

  const parseDateValue = useCallback((dateValue) => {
    if (!dateValue) return null;

    if (dayjs.isDayjs(dateValue)) {
      return dateValue.isValid() ? dateValue : null;
    }

    const parsed = dayjs(dateValue);
    return parsed.isValid() ? parsed : null;
  }, []);
  const parsedValue = parseDateValue(value);

  const showRequiredError = required && !parsedValue && !isFocused && (showRequiredImmediately || touched);

  const validationError = internalError && !isFocused ? internalError : null;

  const errorText = externalErrorText || validationError || (showRequiredError ? "Required" : "");
  const showError = Boolean(errorText);

  const handleChange = useCallback(
    (newValue) => {
      if (internalError) {
        setInternalError(null);
      }

      if (!newValue) {
        setHasInputAttempt(false);
        if (onChange) {
          onChange({
            target: {
              name,
              value: null,
            },
          });
        }
        return;
      }

      setHasInputAttempt(true);

      if (newValue.isValid()) {
        const formattedValue = newValue.format("YYYY-MM-DD");
        if (onChange) {
          onChange({
            target: {
              name,
              value: formattedValue,
            },
          });
        }
      }
    },
    [name, onChange, internalError]
  );

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    if (internalError) {
      setInternalError(null);
    }
  }, [internalError]);

  const handleAccept = useCallback(
    (acceptedValue) => {
      if (!touched) {
        setInternalTouched(true);
        if (onFieldTouched) {
          onFieldTouched(name);
        }
      }

      if (!acceptedValue) {
        return;
      }

      if (acceptedValue.isValid()) {
        setHasInputAttempt(false);
        setInternalError(null);
        const formattedValue = acceptedValue.format("YYYY-MM-DD");
        const oldDayjsValue = parseDateValue(value);

        if (!oldDayjsValue || !oldDayjsValue.isSame(acceptedValue, "day")) {
          if (onChange) {
            onChange({
              target: {
                name,
                value: formattedValue,
              },
            });
          }
        }
      }
    },
    [name, onChange, touched, onFieldTouched, value, parseDateValue]
  );

  const handleBlur = useCallback(
    (e) => {
      setIsFocused(false);

      if (!touched) {
        setInternalTouched(true);
        if (onFieldTouched) {
          onFieldTouched(name);
        }
      }

      let isInvalidDate = false;

      if (parsedValue && parsedValue.isValid()) {
        const year = parsedValue.year();
        if (year < MIN_YEAR || year > MAX_YEAR) {
          isInvalidDate = true;
        }
      } else if (!parsedValue && hasInputAttempt) {
        isInvalidDate = true;
      }

      if (isInvalidDate) {
        setInternalError("Invalid date");
        if (onChange) {
          onChange({
            target: {
              name,
              value: "",
            },
          });
        }
      } else {
        setInternalError(null);
        if (parsedValue) {
          setHasInputAttempt(false);
        }
      }

      if (slotProps?.textField?.onBlur) {
        slotProps.textField.onBlur(e);
      }
    },
    [name, parsedValue, hasInputAttempt, touched, onFieldTouched, slotProps, onChange]
  );

  const handleClear = useCallback(() => {
    if (!touched) {
      setInternalTouched(true);
      if (onFieldTouched) {
        onFieldTouched(name);
      }
    }

    setHasInputAttempt(false);
    setInternalError(null);

    if (onChange) {
      onChange({
        target: {
          name,
          value: null,
        },
      });
    }
  }, [name, onChange, touched, onFieldTouched]);

  return (
    <SkeletonWrapper variant="textfield" sx={{ marginBottom: "20px" }} isLoading={isLoading}>
      <FormControl fullWidth error={showError} sx={sx} className={className} data-testid={`${name}-date-picker`}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            {...otherProps}
            name={name}
            label={label}
            format={DATE_DISPLAY_FORMAT}
            value={parsedValue}
            onChange={handleChange}
            onAccept={handleAccept}
            minDate={dayjs().subtract(50, "year")}
            maxDate={dayjs().add(80, "year")}
            disabled={disabled}
            slots={{
              textField: TextField,
            }}
            slotProps={{
              textField: {
                name,
                fullWidth: true,
                InputLabelProps: { shrink: true },
                error: showError,
                helperText:
                  errorText || (slotProps?.textField?.helperText !== undefined ? slotProps.textField.helperText : " "),
                FormHelperTextProps: {
                  id: `${name}-error`,
                  "data-testid": `${name}-error`,
                  ...slotProps?.textField?.FormHelperTextProps,
                },
                onFocus: (e) => {
                  handleFocus();
                  if (slotProps?.textField?.onFocus) {
                    slotProps.textField.onFocus(e);
                  }
                },
                onBlur: handleBlur,
                InputProps: {
                  ...slotProps?.textField?.InputProps,
                },
                sx: {
                  "& input:not(:focus)::placeholder": {
                    opacity: 0,
                  },
                  ...slotProps?.textField?.sx,
                },
                onKeyDown: (e) => {
                  if (e.key === "Tab") {
                    e.stopPropagation();
                  }
                  if (slotProps?.textField?.onKeyDown) {
                    slotProps.textField.onKeyDown(e);
                  }
                },
                inputRef,
                ...slotProps?.textField,
              },
              openPickerButton: {
                tabIndex: -1,
                ...slotProps?.openPickerButton,
              },
              field: {
                clearable: clearable,
                onClear: handleClear,
                ...slotProps?.field,
              },
              ...slotProps,
            }}
          />
        </LocalizationProvider>
      </FormControl>
    </SkeletonWrapper>
  );
};
